import { IDENTITY_POLICY } from "../policy";

/** Anything with getRandomValues: Web Crypto in the browser and in Node. Injected so tests can be deterministic. */
export type RandomSource = Pick<Crypto, "getRandomValues">;

const BASE64URL = /^[A-Za-z0-9_-]+$/;
const BITS_PER_BASE64_CHAR = 6;
const BYTE_RANGE = 256;

export function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A random base64url token; 16 bytes give the 128 bits FR-ADMIN-032 and FR-COLLAB-101 require. */
export function randomToken(
  source: RandomSource,
  bytes: number = IDENTITY_POLICY.tokenBytes,
): string {
  return toBase64Url(source.getRandomValues(new Uint8Array(bytes)));
}

/** Bits of randomness a base64url token can carry, for entropy checks. */
export function tokenBits(token: string): number {
  return BASE64URL.test(token) ? Math.floor(token.length * BITS_PER_BASE64_CHAR) : 0;
}

export function looksLikeToken(token: string): boolean {
  return tokenBits(token) >= IDENTITY_POLICY.tokenBytes * 8;
}

/** Uniform random decimal digits (rejection sampling avoids modulo bias). */
export function randomDigits(source: RandomSource, count: number): string {
  const limit = BYTE_RANGE - (BYTE_RANGE % 10);
  let digits = "";
  while (digits.length < count) {
    for (const byte of source.getRandomValues(new Uint8Array(count))) {
      if (byte < limit && digits.length < count) digits += String(byte % 10);
    }
  }
  return digits;
}

/**
 * SHA-256 as base64url. The mock stores tokens and codes only as these hashes (FR-ADMIN-032). Share-link
 * passwords use it too here; the identity service uses Argon2id for passwords (FR-ADMIN-032).
 */
export async function sha256(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return toBase64Url(new Uint8Array(digest));
}
